import { FileText, Send, MoreHorizontal, ArrowLeft } from "lucide-react";
import React, { useState, useEffect, useCallback } from "react";
import { useCanViewFinancials } from "../hooks/useCanViewFinancials";
import { useParams, useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { isSuperAdmin } from "../hooks/useSuperAdmin";

import { toast } from "sonner";
import { AnimatedPage } from "../BizDevAnimations";
import "../BizDevAnimations.css";
import ShareButton from "../components/ShareButton";
import MobileSkeleton from "../components/MobileSkeleton";
import useShortcuts from "../hooks/useShortcuts";
import useUrlState from "../hooks/useUrlState";

const ASSET_TYPES = ["reels", "statics", "stories", "carousels", "influencers"];

const STATUS_DOT = {
  New: "bg-gray-400", "In Progress": "bg-blue-400", "Concept Sent": "bg-blue-400",
  Feedback: "bg-yellow-400", "In-Production": "bg-orange-400", "Post-Production": "bg-purple-400",
  Executed: "bg-green-400", "Asset Live": "bg-green-400", Won: "bg-green-400",
  Lost: "bg-red-400", Cancelled: "bg-red-400", Pitched: "bg-amber-400",
};

const STATUS_BADGE = {
  New: "bg-gray-500/12 text-gray-300", "In Progress": "bg-blue-500/12 text-blue-300",
  "Concept Sent": "bg-blue-500/12 text-blue-300", Feedback: "bg-yellow-500/12 text-yellow-300",
  "In-Production": "bg-orange-500/12 text-orange-300", "Post-Production": "bg-purple-500/12 text-purple-300",
  Executed: "bg-green-500/12 text-green-300", "Asset Live": "bg-green-500/12 text-green-300",
  Won: "bg-green-500/12 text-green-300", Lost: "bg-red-500/10 text-red-300",
  Cancelled: "bg-red-500/10 text-red-300", Pitched: "bg-amber-500/12 text-amber-300",
};

/* ── Delete Button (admin only) ── */
function AccountDeleteButton({ accountId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const isAdmin = (user?.role || "").toLowerCase() === "admin" || isSuperAdmin(user?.email);
  if (!isAdmin) return null;

  const handleDelete = async () => {
    try {
      await api.delete(`/accounts/${accountId}`);
      toast.success("Account deleted");
      navigate("/bizdev/accounts");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <>
      <button onClick={() => setShowDelete(true)} className="text-red-400 text-sm hover:text-red-300 transition-colors">Delete Account</button>
      {showDelete && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--s1)] border border-red-500/30 rounded-2xl p-6 w-full max-w-md">
            <h2 style={{ fontFamily: "var(--font-orb)", fontSize: "18px", color: "white", marginBottom: "12px" }}>DELETE ACCOUNT</h2>
            <p className="text-[#999] text-sm mb-4">Are you sure you want to permanently delete this account? This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDelete(false)} className="flex-1 py-2.5 border border-[var(--bdrh)] rounded-xl text-[#999] text-sm hover:border-[var(--bdrh)]">Cancel</button>
              <button onClick={handleDelete} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-medium">Delete Account</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ── Editable Field ── */
function EditableField({ label, value, onSave, type = "text" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  useEffect(() => { setVal(value || ""); }, [value]);

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input type={type} value={val} onChange={(e) => setVal(e.target.value)} autoFocus
          className="flex-1 px-2 py-1 bg-[var(--s1)] border border-blue-500/50 rounded text-sm text-[var(--t1)] focus:outline-none focus:ring-2 focus:ring-blue-500"
          onKeyDown={(e) => { if (e.key === "Enter") { onSave(val); setEditing(false); } if (e.key === "Escape") setEditing(false); }}
        />
        <button onClick={() => { onSave(val); setEditing(false); }} className="px-2 py-1 bg-blue-600 text-white rounded text-xs">Save</button>
        <button onClick={() => setEditing(false)} className="px-2 py-1 text-[#888] text-xs">Cancel</button>
      </div>
    );
  }

  return (
    <span onClick={() => setEditing(true)} className="cursor-pointer hover:text-blue-400 transition-colors group">
      {value || <span className="text-[#666] italic">Set</span>}
      <span className="ml-1 opacity-0 group-hover:opacity-100 text-blue-400 text-xs">&#9998;</span>
    </span>
  );
}

/* ── SOW Card ── */
function SOWCard({ type, target, delivered, carryover = 0 }) {
  const totalTarget = target + carryover;
  const pct = totalTarget > 0 ? Math.round((delivered / totalTarget) * 100) : 0;
  const isNA = target === 0 && carryover === 0;
  const color = isNA ? "gray" : pct >= 70 ? "green" : pct >= 40 ? "amber" : "red";
  const chipLabel = isNA ? "Not in SOW" : pct >= 70 ? "On track" : pct >= 40 ? "Behind" : "At risk";
  const chipIcon = isNA ? "" : pct >= 70 ? "\u2713" : pct >= 40 ? "\u26A0" : "\u2717";

  const fillColors = { green: "bg-gradient-to-r from-green-600 to-green-400", amber: "bg-gradient-to-r from-amber-600 to-amber-400", red: "bg-gradient-to-r from-red-600 to-red-400", gray: "" };
  const pctColors = { green: "text-green-300", amber: "text-amber-300", red: "text-red-300", gray: "text-[#555]" };
  const chipColors = { green: "bg-green-500/10 text-green-300", amber: "bg-amber-500/10 text-amber-300", red: "bg-red-500/10 text-red-300", gray: "bg-[#ffffff08] text-[#666]" };

  return (
    <div className={`bg-[var(--s1)] border border-[var(--bdr)] rounded-[14px] p-5 transition-all hover:border-[var(--bdrh)] hover:bg-[var(--s1)] ${isNA ? "opacity-50" : ""}`}>
      <div className="flex justify-between items-center mb-3">
        <span className="text-[13px] text-[var(--t3)] capitalize font-medium">{type}</span>
        <span className={`text-[12px] font-semibold font-mono ${pctColors[color]}`}>{isNA ? "\u2014" : `${pct}%`}</span>
      </div>
      <div className="flex items-baseline gap-1 mb-3">
        <span className={`font-heading text-[28px] font-bold ${isNA ? "text-[var(--t3)]" : "text-white"}`}>{delivered}</span>
        <span className="text-[15px] text-[#555]">/</span>
        <span className="text-[15px] text-[#888]">{totalTarget}</span>
      </div>
      {carryover > 0 && (
        <p className="text-[10px] text-amber-400/70 mb-2">{target} target + {carryover} carryover</p>
      )}
      <div className="h-2 bg-[var(--s2)] rounded overflow-hidden">
        <div className={`h-full rounded ${fillColors[color]}`} style={{ width: `${Math.min(pct, 100)}%`, transition: "width 0.8s cubic-bezier(.4,0,.2,1)" }} />
      </div>
      <div className={`inline-flex items-center gap-1 mt-2.5 text-[11px] font-semibold px-2.5 py-1 rounded-md ${chipColors[color]}`}>
        {chipIcon} {chipLabel}
      </div>
    </div>
  );
}

/* ── SOW Edit Modal ── */
function SOWEditModal({ sow, delivered, monthLabel, onSaveSow, onSaveDelivered, onClose }) {
  const [activeEditTab, setActiveEditTab] = useState("delivered");
  const [sowVals, setSowVals] = useState(sow || { reels: 0, statics: 0, stories: 0, carousels: 0, influencers: 0 });
  const [delVals, setDelVals] = useState(delivered || { reels: 0, statics: 0, stories: 0, carousels: 0, influencers: 0 });

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-2xl p-6 w-full max-w-lg">
        <h2 className="font-heading text-lg text-[var(--t1)] mb-1">Update SOW</h2>
        {monthLabel && (
          <p className="text-[11px] text-[#888] font-mono uppercase tracking-[1.5px] mb-4">
            Editing · {monthLabel}
          </p>
        )}
        <div className="flex gap-2 mb-5">
          <button onClick={() => setActiveEditTab("delivered")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeEditTab === "delivered" ? "bg-red-500/15 text-red-300 border border-red-500/30" : "text-[#888] border border-[var(--bdr)]"}`}>
            Delivered ({monthLabel || "this month"})
          </button>
          <button onClick={() => setActiveEditTab("targets")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeEditTab === "targets" ? "bg-blue-500/15 text-blue-300 border border-blue-500/30" : "text-[#888] border border-[var(--bdr)]"}`}>
            Monthly Targets
          </button>
        </div>

        {activeEditTab === "delivered" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            {ASSET_TYPES.map((t) => (
              <div key={t}>
                <label className="text-xs text-[#AAA] capitalize font-medium">{t}</label>
                <input type="number" min="0" value={delVals[t] || 0}
                  onChange={(e) => setDelVals((prev) => ({ ...prev, [t]: parseInt(e.target.value) || 0 }))}
                  className="w-full mt-1 px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50"
                />
              </div>
            ))}
          </div>
        )}

        {activeEditTab === "targets" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            {ASSET_TYPES.map((t) => (
              <div key={t}>
                <label className="text-xs text-[#AAA] capitalize font-medium">{t}</label>
                <input type="number" min="0" value={sowVals[t] || 0}
                  onChange={(e) => setSowVals((prev) => ({ ...prev, [t]: parseInt(e.target.value) || 0 }))}
                  className="w-full mt-1 px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-[var(--bdrh)] rounded-xl text-[#999] text-sm hover:border-[var(--bdrh)]">Cancel</button>
          <button onClick={() => {
            if (activeEditTab === "delivered") onSaveDelivered(delVals);
            else onSaveSow(sowVals);
            onClose();
          }} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-medium">
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── More Actions Menu ── */
function MoreMenu({ accountId, account, onSaveField }) {
  const [open, setOpen] = useState(false);
  const [editField, setEditField] = useState(null);

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="w-10 h-10 rounded-[10px] border border-[var(--bdrh)] bg-[var(--s1)] text-[#999] flex items-center justify-center hover:border-[#444] hover:text-[#ddd] hover:bg-[var(--s2)] transition-all">
        <MoreHorizontal size={18} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-12 z-50 bg-[var(--s1)] border border-[var(--bdr)] rounded-xl shadow-2xl w-56 py-2">
            <button onClick={() => { setEditField("account_manager"); setOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-[var(--t3)] hover:bg-[var(--s2)] transition-colors">Edit Account Manager</button>
            <button onClick={() => { setEditField("website"); setOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-[var(--t3)] hover:bg-[var(--s2)] transition-colors">Edit Website</button>
            <button onClick={() => { setEditField("company_name"); setOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-[var(--t3)] hover:bg-[var(--s2)] transition-colors">Edit Company Name</button>
            <button onClick={() => { setEditField("notes"); setOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-[var(--t3)] hover:bg-[var(--s2)] transition-colors">Edit Notes</button>
            <button onClick={() => { setEditField("monthly_retainer_value"); setOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-[var(--t3)] hover:bg-[var(--s2)] transition-colors">Edit Monthly Fee</button>
            <div className="border-t border-[var(--bdr)] my-1" />
            <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider text-[#555] font-bold">Brand profile (AI thumbnails)</div>
            <button onClick={() => { setEditField("vertical"); setOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-[var(--t3)] hover:bg-[var(--s2)] transition-colors">Edit Vertical</button>
            <button onClick={() => { setEditField("aesthetic_keywords"); setOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-[var(--t3)] hover:bg-[var(--s2)] transition-colors">Edit Aesthetic Keywords</button>
            <button onClick={() => { setEditField("cast_and_location_notes"); setOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-[var(--t3)] hover:bg-[var(--s2)] transition-colors">Edit Cast &amp; Location</button>
            <button onClick={() => { setEditField("logo_url_override"); setOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-[var(--t3)] hover:bg-[var(--s2)] transition-colors">Override Logo URL</button>
            <div className="border-t border-[var(--bdr)] my-1" />
            <AccountDeleteButton accountId={accountId} />
          </div>
        </>
      )}
      {editField && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-2xl p-6 w-full max-w-md">
            <h2 className="font-heading text-lg text-[var(--t1)] mb-4 capitalize">Edit {editField.replace(/_/g, " ")}</h2>
            <EditableInlineModal field={editField} value={account[editField]} onSave={(v) => { onSaveField(editField, editField === "monthly_retainer_value" ? parseFloat(v) || 0 : v); setEditField(null); }} onClose={() => setEditField(null)} />
          </div>
        </div>
      )}
    </div>
  );
}

// Verticals supported by the AI thumbnail prompt builder. Keep in sync with
// _VERTICAL_PRESETS keys in backend/server.py — adding one without a preset
// will fall through to the Default mood, which is fine but not branded.
const BRAND_VERTICALS = [
  "Auto", "Jewelry", "Fashion", "Food", "Fintech", "Beauty", "Telco",
  "FMCG", "Tech", "Travel", "Real Estate", "Healthcare", "Education",
];

// Long-form fields render a textarea instead of a single-line input.
const TEXTAREA_FIELDS = new Set(["aesthetic_keywords", "cast_and_location_notes", "notes"]);

// Field-specific helper text shown under the input.
const FIELD_HINTS = {
  vertical: "Drives the AI thumbnail's mood + cast/location defaults.",
  aesthetic_keywords: "5-10 keywords describing the brand's mood. Example: \"premium, golden, festive, intimate, traditional craftsmanship\"",
  cast_and_location_notes: "Couple of sentences. Example: \"Modern urban Indian woman, mid-30s, Mumbai monsoon street, golden hour\"",
  logo_url_override: "Use only if Google's favicon for this brand looks wrong. Otherwise leave blank.",
};

function EditableInlineModal({ field, value, onSave, onClose }) {
  const [val, setVal] = useState(value || "");
  const isTextarea = TEXTAREA_FIELDS.has(field);
  const isVertical = field === "vertical";
  const hint = FIELD_HINTS[field];
  return (
    <>
      {isVertical ? (
        <select value={val} onChange={(e) => setVal(e.target.value)} autoFocus
          className="w-full px-3 py-2 bg-[var(--s1)] border border-[var(--bdrh)] rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 mb-2">
          <option value="">— Auto-detect from company name —</option>
          {BRAND_VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      ) : isTextarea ? (
        <textarea value={val} onChange={(e) => setVal(e.target.value)} autoFocus rows={4}
          className="w-full px-3 py-2 bg-[var(--s1)] border border-[var(--bdrh)] rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 mb-2 resize-y"
          onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
        />
      ) : (
        <input type={field === "monthly_retainer_value" ? "number" : "text"} value={val} onChange={(e) => setVal(e.target.value)} autoFocus
          className="w-full px-3 py-2 bg-[var(--s1)] border border-[var(--bdrh)] rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 mb-2"
          onKeyDown={(e) => { if (e.key === "Enter") onSave(val); if (e.key === "Escape") onClose(); }}
        />
      )}
      {hint && <p className="text-[11px] text-[#777] mb-3 leading-relaxed">{hint}</p>}
      {!hint && <div className="mb-2" />}
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 py-2.5 border border-[var(--bdrh)] rounded-xl text-[#999] text-sm">Cancel</button>
        <button onClick={() => onSave(val)} className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium">Save</button>
      </div>
    </>
  );
}

/* ══════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════ */
export default function AccountDetailPage() {
  const { id } = useParams();
  const accountId = id;
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const canViewFinancials = useCanViewFinancials();
  const [briefs, setBriefs] = useState([]);
  const [retainerMonths, setRetainerMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Active tab in URL — shareable deep-links + reload-safe
  const [activeTab, setActiveTab] = useUrlState("tab", "briefs");
  const [showSOWEdit, setShowSOWEdit] = useState(false);
  const [sowDelivered, setSowDelivered] = useState({ reels: 0, statics: 0, stories: 0, carousels: 0, influencers: 0 });
  const [sowCarryover, setSowCarryover] = useState({ reels: 0, statics: 0, stories: 0, carousels: 0, influencers: 0 });
  // Per-month delivered tracking. Default to the current month so existing
  // top-level sow_delivered semantics map cleanly to the active tab.
  const [sowDeliveredByMonth, setSowDeliveredByMonth] = useState({});  // { "2026-04": {...}, ... }
  const [sowTargetsByMonth, setSowTargetsByMonth] = useState({});      // { "2026-04": {reels:N,…}, … }
  const [selectedSowMonth, setSelectedSowMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [tasks, setTasks] = useState([]);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertForm, setConvertForm] = useState({
    fee: "",
    startDate: new Date().toISOString().split("T")[0],
    serviceType: "Social & Digital Retainer",
    sow: { reels: 0, statics: 0, stories: 0, carousels: 0, influencers: 0 },
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [leadRes, briefsRes, tasksRes] = await Promise.all([
        api.get(`/leads/${accountId}`),
        api.get(`/leads/${accountId}/briefs`),
        api.get(`/leads/${accountId}/tasks`).catch(() => ({ data: [] })),
      ]);
      setAccount(leadRes.data);
      setBriefs(briefsRes.data?.briefs || briefsRes.data || []);
      setTasks(tasksRes.data || []);
      if (leadRes.data.sow_delivered) setSowDelivered(leadRes.data.sow_delivered);
      if (leadRes.data.sow_carryover) setSowCarryover(leadRes.data.sow_carryover);
      // Hydrate the per-month map. If empty, seed with the current month
      // from the legacy top-level sow_delivered so the tabs UI is never
      // empty on existing accounts.
      const byMonth = leadRes.data.sow_delivered_by_month || {};
      if (Object.keys(byMonth).length === 0 && leadRes.data.sow_delivered) {
        const d = new Date();
        const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        byMonth[ym] = leadRes.data.sow_delivered;
      }
      setSowDeliveredByMonth(byMonth);

      // Same shape for targets — hydrate per-month from legacy retainer_sow
      // when empty. Without this, every month inherits the same retainer_sow
      // and editing one month's targets was overwriting all of them.
      const targetsByMonth = leadRes.data.retainer_sow_by_month || {};
      setSowTargetsByMonth(targetsByMonth);

      if (leadRes.data.is_retainer) {
        try {
          const rmRes = await api.get(`/retainer-months/${accountId}`);
          setRetainerMonths(rmRes.data || []);
        } catch (_) { setRetainerMonths([]); }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load account");
    } finally { setLoading(false); }
  }, [accountId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Keyboard shortcuts — tab nav (1-4) + Esc close modals. Desktop only.
  useShortcuts(
    [
      { keys: "1", description: "Briefs tab", group: "Account",
        handler: () => setActiveTab("briefs") },
      { keys: "2", description: "Tasks tab", group: "Account",
        handler: () => setActiveTab("tasks") },
      { keys: "3", description: "SOW tab", group: "Account",
        handler: () => setActiveTab("sow") },
      { keys: "4", description: "Activity tab", group: "Account",
        handler: () => setActiveTab("activity") },
      { keys: "escape", description: "Close modal / delete",
        handler: () => {
          setShowSOWEdit((v) => v ? false : v);
          setShowConvertModal((v) => v ? false : v);
          setShowDelete((v) => v ? false : v);
        } },
    ],
    { enabled: !!account && !loading }
  );
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const saveField = async (field, value) => {
    try {
      await api.put(`/leads/${accountId}`, { [field]: value });
      setAccount((prev) => ({ ...prev, [field]: value }));
      toast.success("Saved");
    } catch (err) { toast.error("Error saving"); }
  };

  const saveSOW = async (sowData) => {
    // Write targets to the per-month map so editing March doesn't overwrite
    // April. Only mirror to the legacy top-level retainer_sow when the
    // active tab is the current calendar month — keeps the existing
    // "current-month progress bar" reads consistent without smearing
    // historical or future targets.
    try {
      const now = new Date();
      const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const isCurrentMonth = selectedSowMonth === currentYm;
      const nextByMonth = { ...sowTargetsByMonth, [selectedSowMonth]: sowData };
      const payload = { retainer_sow_by_month: nextByMonth };
      if (isCurrentMonth) payload.retainer_sow = sowData;
      await api.put(`/leads/${accountId}`, payload);
      setSowTargetsByMonth(nextByMonth);
      if (isCurrentMonth) setAccount((prev) => ({ ...prev, retainer_sow: sowData }));
      toast.success(`Targets saved · ${selectedSowMonth}`);
    } catch (err) { toast.error("Error saving SOW targets"); }
  };

  const saveDelivered = async (delData) => {
    try {
      // Save the entry against the currently-selected month tab. We also
      // mirror to the legacy top-level sow_delivered ONLY when the active
      // tab is the current calendar month — that keeps the existing
      // overall-bar / current-month read paths consistent without
      // overwriting historical values when an admin edits a past month.
      const now = new Date();
      const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const isCurrentMonth = selectedSowMonth === currentYm;
      const nextByMonth = { ...sowDeliveredByMonth, [selectedSowMonth]: delData };
      const payload = { sow_delivered_by_month: nextByMonth };
      if (isCurrentMonth) payload.sow_delivered = delData;
      await api.put(`/leads/${accountId}`, payload);
      setSowDeliveredByMonth(nextByMonth);
      if (isCurrentMonth) setSowDelivered(delData);
      toast.success(`Saved ${selectedSowMonth}`);
    } catch (err) { toast.error("Error saving delivered counts"); }
  };

  if (loading) return window.innerWidth < 768 ? <MobileSkeleton type="detail" /> : <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div></div>;
  if (error || !account) return (
    <div className="p-6 text-center">
      <p className="text-red-400 mb-3">{error || "Account not found"}</p>
      <button onClick={() => navigate("/bizdev/accounts")} className="text-sm text-[#888] hover:text-[var(--t1)] transition-colors">&larr; Back to Accounts</button>
    </div>
  );

  const isRetainer = account.is_retainer;
  // Display SOW targets for the currently-selected month, falling back to
  // the lead-level retainer_sow only when that month has no override.
  // Without this fallback chain, switching months would either show nothing
  // (per-month not set) or show the wrong shared blob for every month.
  const sow = sowTargetsByMonth[selectedSowMonth] || account.retainer_sow || {};
  const adhocTarget = isRetainer ? Math.round((account.monthly_retainer_value || 0) * 0.5) : 0;
  const totalBilled = briefs.filter((b) => b.status === "Asset Live" || b.status === "Executed").reduce((sum, b) => sum + (b.budget || 0), 0);
  const activeBriefs = briefs.filter((b) => !["Asset Live", "Cancelled", "Lost", "Executed"].includes(b.status));
  const executedBriefs = briefs.filter((b) => b.status === "Asset Live" || b.status === "Executed");
  const lostBriefs = briefs.filter((b) => b.status === "Lost" || b.status === "Cancelled");

  // SOW overall
  const sowTotal = ASSET_TYPES.reduce((s, t) => s + (sow[t] || 0), 0);
  const sowDeliveredTotal = ASSET_TYPES.reduce((s, t) => s + (sowDelivered[t] || 0), 0);
  const sowPct = sowTotal > 0 ? Math.round((sowDeliveredTotal / sowTotal) * 100) : 0;

  const adhocPct = adhocTarget > 0 ? Math.min(Math.round((totalBilled / adhocTarget) * 100), 100) : 0;

  const now = new Date();
  const monthName = now.toLocaleString("en-IN", { month: "long", year: "numeric" });

  // ─────────── SOW MONTHLY TABS ───────────
  // Generate the month range Jan-2026 → current month (inclusive). When the
  // calendar rolls into 2027, this naturally extends.
  const sowMonthList = (() => {
    const out = [];
    const startY = 2026, startM = 1;  // Jan 2026 baseline
    const endY = now.getFullYear();
    const endM = now.getMonth() + 1;
    for (let y = startY; y <= endY; y++) {
      const fromM = y === startY ? startM : 1;
      const toM = y === endY ? endM : 12;
      for (let m = fromM; m <= toM; m++) {
        out.push(`${y}-${String(m).padStart(2, "0")}`);
      }
    }
    return out;
  })();
  const sowMonthLabel = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "short", year: "numeric" });
  };
  const sowMonthLabelLong = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
  };
  // Displayed delivered comes from the per-month map for any selected tab;
  // falls back to the legacy single-blob if the tab is empty (covers the
  // case where the user hasn't yet edited a historical month).
  const displayedDelivered = sowDeliveredByMonth[selectedSowMonth] || {};
  const displayedDeliveredTotal = ASSET_TYPES.reduce((s, t) => s + (displayedDelivered[t] || 0), 0);

  return (
    <AnimatedPage>
    <div className="max-w-[1200px] mx-auto px-6 py-7">

      {/* Back */}
      <button onClick={() => navigate("/bizdev/accounts")} className="inline-flex items-center gap-1.5 text-[#888] text-[12px] font-mono uppercase tracking-[1.5px] mb-6 hover:text-[var(--r)] transition-colors">
        <ArrowLeft size={14} /> Accounts
      </button>

      {/* ═══ HERO ═══ */}
      <div className="dm-detail-header flex justify-between items-start mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            {account.website && account.website !== "Unknown" && (
              <img src={`https://www.google.com/s2/favicons?domain=${account.website.replace(/^https?:\/\//, "").replace(/\/.*$/, "")}&sz=128`}
                width={44} height={44} alt={account.company_name}
                className="rounded-xl object-contain"
                style={{ background: "var(--s3)" }}
                onError={e => { e.target.style.display = "none"; }} />
            )}
            <h1 className="font-heading text-[32px] font-extrabold text-[var(--t1)] tracking-wide leading-tight dm-account-title">{account.company_name}</h1>
          </div>
          <div className="flex items-center gap-2.5 mt-3 flex-wrap dm-account-meta">
            {isRetainer ? (
              <span className="px-3.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[1.2px] bg-gradient-to-r from-indigo-500/15 to-purple-500/15 text-purple-300 border border-purple-500/25">Retainer</span>
            ) : (
              <span className="px-3.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[1.2px] bg-gray-500/12 text-gray-400 border border-gray-500/20">Adhoc</span>
            )}
            <span className="px-3.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[1.2px] bg-green-500/10 text-green-300 border border-green-500/20">&#9679; Active</span>
            <span className="text-[var(--t3)] text-[10px]">&middot;</span>
            <span className="text-[13px] text-[#AAA]">AM: <EditableField value={account.account_manager} onSave={(v) => saveField("account_manager", v)} /></span>
            <span className="text-[var(--t3)] text-[10px]">&middot;</span>
            <span className="text-[13px]">
              {account.website ? <a href={account.website.startsWith("http") ? account.website : `https://${account.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:text-blue-200 hover:underline transition-colors">{account.website.replace(/^https?:\/\//, "")}</a> : <span className="text-[#666]">No website</span>}
            </span>
          </div>
        </div>
        <div className="dm-detail-actions flex items-center gap-2">
          {!isRetainer && (
            <button onClick={() => setShowConvertModal(true)} className="flex items-center gap-1.5 px-5 py-2.5 rounded-[10px] border border-purple-500/35 bg-purple-500/8 text-purple-300 text-[12px] font-semibold font-mono uppercase tracking-[0.8px] hover:bg-purple-500/15 hover:border-purple-500/50 transition-all">Convert to Retainer</button>
          )}
          <button onClick={() => navigate(`/bizdev/briefs/new?lead_id=${accountId}`)} className="flex items-center gap-1.5 px-5 py-2.5 rounded-[10px] border border-red-500/35 bg-red-500/8 text-red-300 text-[12px] font-semibold font-mono uppercase tracking-[0.8px] hover:bg-red-500/15 hover:border-red-500/50 transition-all">+ New Brief</button>
          <button onClick={() => navigate(`/tasks/new?lead_id=${accountId}`)} className="w-10 h-10 rounded-[10px] border border-[var(--bdrh)] bg-[var(--s1)] text-[#999] flex items-center justify-center hover:border-[#444] hover:text-[#ddd] hover:bg-[var(--s2)] transition-all" title="Create Task"><FileText size={16} /></button>
          <MoreMenu accountId={accountId} account={account} onSaveField={saveField} />
          <ShareButton title={account?.company_name} text={`Account: ${account?.company_name}`} />
        </div>
      </div>

      {/* ═══ REVENUE STRIP ═══ */}
      <div className="grid grid-cols-1 md:grid-cols-3 bg-[var(--s1)] border border-[var(--bdr)] rounded-2xl overflow-hidden mb-8">
        {/* Monthly Retainer */}
        <div className="p-7 relative">
          <div className="text-[10px] font-mono uppercase tracking-[1.5px] text-[#888] font-medium mb-2.5">Monthly Retainer</div>
          <div className="flex items-baseline gap-2.5">
            <span className="font-heading text-[32px] font-bold text-blue-300">₹{((account.monthly_retainer_value || 0) / 100000).toLocaleString("en-IN", { maximumFractionDigits: 2 })}<span className="text-[18px] text-blue-400/70">L</span></span>
            <span className="text-[14px] text-[var(--t3)]">/ month</span>
          </div>
          <div className="text-[12px] text-[var(--t3)] mt-2">{account.lead_source || "Direct Lead"} {account.retainer_start_date ? `\u00B7 Since ${account.retainer_start_date}` : ""}</div>
        </div>

        {/* Adhoc Revenue */}
        <div className="p-7 border-l border-[var(--bdr)]">
          <div className="text-[10px] font-mono uppercase tracking-[1.5px] text-[#888] font-medium mb-2.5">Adhoc Revenue (CY)</div>
          <div className="flex items-baseline gap-2.5">
            <span className="font-heading text-[32px] font-bold text-amber-300">₹{(totalBilled / 100000).toLocaleString("en-IN", { maximumFractionDigits: 2 })}<span className="text-[18px] text-amber-400/70">L</span></span>
            <span className="text-[14px] text-[var(--t3)]">of ₹{(adhocTarget / 100000).toLocaleString("en-IN", { maximumFractionDigits: 2 })}L target</span>
          </div>
          <div className="mt-3.5">
            <div className="h-2.5 bg-[var(--s2)] rounded-[5px] overflow-hidden">
              <div className="h-full rounded-[5px] bg-gradient-to-r from-amber-600 to-amber-400" style={{ width: `${adhocPct}%`, transition: "width 0.8s cubic-bezier(.4,0,.2,1)" }} />
            </div>
            <div className="flex justify-between mt-1.5 text-[11px] text-[var(--t3)] font-mono">₹{(totalBilled / 100000).toLocaleString("en-IN", { maximumFractionDigits: 2 })}L<span>₹{(adhocTarget / 100000).toLocaleString("en-IN", { maximumFractionDigits: 2 })}L</span></div>
          </div>
        </div>

        {/* Brief Ring */}
        <div className="p-7 border-l border-[var(--bdr)]">
          <div className="text-[10px] font-mono uppercase tracking-[1.5px] text-[#888] font-medium mb-2.5">Brief Breakdown</div>
          <div className="flex items-center gap-5">
            <svg width="88" height="88" viewBox="0 0 88 88">
              <circle cx="44" cy="44" r="33" fill="none" stroke="#1A1A1A" strokeWidth="9" />
              {(() => {
                const segments = [
                  { count: activeBriefs.length, color: "#60A5FA" },
                  { count: executedBriefs.length, color: "#4ADE80" },
                  { count: lostBriefs.length, color: "#F87171" },
                ];
                const total = briefs.length || 1;
                const circ = 2 * Math.PI * 33;
                let offset = 0;
                return segments.filter(s => s.count > 0).map((s, i) => {
                  const len = (s.count / total) * circ;
                  const el = <circle key={i} cx="44" cy="44" r="33" fill="none" stroke={s.color} strokeWidth="9" strokeDasharray={`${len} ${circ - len}`} strokeDashoffset={-offset} transform="rotate(-90 44 44)" />;
                  offset += len;
                  return el;
                });
              })()}
              <text x="44" y="41" textAnchor="middle" className="font-heading text-[24px] font-bold" fill="#fff">{briefs.length}</text>
              <text x="44" y="54" textAnchor="middle" className="text-[9px] uppercase tracking-[1px] font-mono" fill="#888">Briefs</text>
            </svg>
            <div className="flex flex-col gap-2">
              {activeBriefs.length > 0 && <div className="flex items-center gap-2 text-[13px] text-[var(--t3)]"><span className="w-2.5 h-2.5 rounded-full bg-blue-400 inline-block" />{activeBriefs.length} Active</div>}
              {executedBriefs.length > 0 && <div className="flex items-center gap-2 text-[13px] text-[var(--t3)]"><span className="w-2.5 h-2.5 rounded-full bg-green-400 inline-block" />{executedBriefs.length} Executed</div>}
              {lostBriefs.length > 0 && <div className="flex items-center gap-2 text-[13px] text-[var(--t3)]"><span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block" />{lostBriefs.length} Lost</div>}
            </div>
          </div>
        </div>
      </div>

      {/* ═══ SOW DELIVERY ═══ */}
      {isRetainer && (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-3.5">
              <span className="text-[11px] font-mono uppercase tracking-[2px] text-[#AAA] font-medium">SOW Delivery</span>
              <span className="text-[10px] text-[#999] font-mono bg-[var(--s1)] px-3 py-1 rounded-md border border-[var(--bdr)]">
                {sowMonthLabelLong(selectedSowMonth)}
              </span>
            </div>
            <button onClick={() => setShowSOWEdit(true)} className="text-[11px] text-red-300 font-mono uppercase tracking-[1px] font-medium px-4 py-1.5 rounded-lg border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 hover:border-red-500/30 transition-all">
              &#9998; Update {sowMonthLabel(selectedSowMonth)}
            </button>
          </div>

          {/* Monthly tabs — Jan 2026 → current. Lets the team fill in past
              months retrospectively without losing the live view. */}
          <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1 dm-sow-tabs" style={{ scrollbarWidth: "thin" }}>
            {sowMonthList.map((ym) => {
              const active = ym === selectedSowMonth;
              const hasData = !!sowDeliveredByMonth[ym] && Object.values(sowDeliveredByMonth[ym]).some((v) => v > 0);
              return (
                <button
                  key={ym}
                  onClick={() => setSelectedSowMonth(ym)}
                  className={`relative px-3.5 py-2 text-[11px] font-mono uppercase tracking-[1px] font-medium rounded-lg border transition-all whitespace-nowrap ${
                    active
                      ? "bg-red-500/10 text-red-300 border-red-500/30"
                      : "bg-[var(--s1)] text-[#999] border-[var(--bdr)] hover:text-[var(--t1)] hover:border-[var(--bdrh)]"
                  }`}
                  title={sowMonthLabelLong(ym) + (hasData ? "" : " — empty")}
                >
                  {sowMonthLabel(ym)}
                  {hasData && (
                    <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-green-400" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 dm-sow-grid">
            {ASSET_TYPES.map((t) => (
              <SOWCard
                key={t}
                type={t}
                target={sow[t] || 0}
                delivered={displayedDelivered[t] || 0}
                carryover={sowCarryover[t] || 0}
              />
            ))}
          </div>

          {/* Overall bar */}
          {sowTotal > 0 && (
            <div className="flex items-center gap-4 mt-5 px-5 py-3.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-xl">
              <span className="text-[11px] text-[#888] font-mono uppercase tracking-[1px] font-medium whitespace-nowrap">Overall</span>
              <div className="flex-1">
                <div className="h-2.5 bg-[var(--s2)] rounded-[5px] overflow-hidden flex">
                  {ASSET_TYPES.map((t) => {
                    const seg = sow[t] || 0;
                    if (seg === 0) return null;
                    const del = displayedDelivered[t] || 0;
                    const pct = (del / sowTotal) * 100;
                    const color = del >= seg * 0.7 ? "bg-green-500" : del >= seg * 0.4 ? "bg-amber-500" : "bg-red-500";
                    return <div key={t} className={`h-full ${color}`} style={{ width: `${pct}%` }} />;
                  })}
                </div>
              </div>
              <span className="text-[13px] text-[var(--t3)] font-mono font-semibold whitespace-nowrap">{displayedDeliveredTotal} / {sowTotal}</span>
              <span className="text-[11px] text-[var(--t3)] whitespace-nowrap">delivered</span>
            </div>
          )}
        </div>
      )}

      {/* ═══ TABS ═══ */}
      <div className="flex border-b border-[var(--bdr)] mb-5 dm-tab-bar">
        {["briefs", "tasks", "sow", "activity"].map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 text-[12px] font-mono uppercase tracking-[1.2px] font-medium border-b-2 transition-all ${activeTab === tab ? "text-red-300 border-[var(--r)]" : "text-[#666] border-transparent hover:text-[#BBB]"}`}>
            {tab === "briefs" ? <>Briefs <span className="ml-1.5 text-[10px] bg-red-500/12 text-red-300 px-2 py-0.5 rounded-full font-semibold">{briefs.length}</span></> : tab === "tasks" ? <>Tasks <span className="ml-1.5 text-[10px] bg-purple-500/12 text-purple-300 px-2 py-0.5 rounded-full font-semibold">{tasks.length}</span></> : tab === "sow" ? "SOW History" : "Activity"}
          </button>
        ))}
      </div>

      {/* ═══ BRIEFS LIST ═══ */}
      {activeTab === "briefs" && (
        <div className="flex flex-col gap-1.5">
          {briefs.length === 0 ? (
            <div className="text-center py-16 text-[#666]">
              <p className="text-sm">No briefs yet for this account</p>
              <button onClick={() => navigate(`/bizdev/briefs/new?lead_id=${accountId}`)} className="mt-3 text-sm text-red-400 hover:text-red-300 transition-colors">+ Create first brief</button>
            </div>
          ) : briefs.map((b) => {
            const dotClass = STATUS_DOT[b.status] || "bg-gray-400";
            const badgeClass = STATUS_BADGE[b.status] || "bg-gray-500/12 text-gray-300";
            const date = b.created_at ? new Date(b.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "";
            return (
              <div key={b.brief_id} onClick={() => navigate(`/bizdev/briefs/${b.brief_id}`)}
                className="grid grid-cols-[12px_1fr_auto_auto_auto] items-center gap-4 px-5 py-4 bg-[var(--s1)] border border-[var(--bdr)] rounded-xl cursor-pointer hover:border-[var(--bdrh)] hover:bg-[var(--s1)] hover:translate-x-[3px] transition-all group">
                <span className={`w-2.5 h-2.5 rounded-full ${dotClass} shadow-[0_0_0_3px_rgba(255,255,255,0.05)]`} />
                <div>
                  <h4 className="text-[14px] font-semibold text-[var(--t1)]">{b.title}</h4>
                  <p className="text-[12px] text-[var(--t3)] mt-0.5">{b.service_type || b.description?.slice(0, 60) || ""}</p>
                </div>
                <span className={`px-3.5 py-1 rounded-md text-[11px] font-semibold ${badgeClass}`}>{b.status}</span>
                <span className="text-[12px] text-[var(--t3)] font-mono min-w-[55px] text-right">{date}</span>
                <span className="text-[var(--t3)] text-[20px] group-hover:text-[var(--r)] group-hover:translate-x-[2px] transition-all">&rsaquo;</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ TASKS TAB ═══ */}
      {activeTab === "tasks" && (
        <div className="flex flex-col gap-1.5">
          {tasks.length === 0 ? (
            <div className="text-center py-16 text-[#666]">
              <p className="text-sm">No tasks linked to this account</p>
              <button onClick={() => navigate(`/tasks/new?lead_id=${accountId}`)} className="mt-3 text-sm text-purple-400 hover:text-purple-300 transition-colors">+ Create first task</button>
            </div>
          ) : tasks.map((t) => {
            const statusDots = { Open: "bg-gray-400", "In Progress": "bg-blue-400", "In Review": "bg-purple-400", Done: "bg-green-400", Blocked: "bg-red-400" };
            const statusBadges = { Open: "bg-gray-500/12 text-gray-300", "In Progress": "bg-blue-500/12 text-blue-300", "In Review": "bg-purple-500/12 text-purple-300", Done: "bg-green-500/12 text-green-300", Blocked: "bg-red-500/10 text-red-300" };
            const dotClass = statusDots[t.status] || "bg-gray-400";
            const badgeClass = statusBadges[t.status] || "bg-gray-500/12 text-gray-300";
            const priorityColors = { High: "text-red-400", Medium: "text-amber-400", Low: "text-gray-400" };
            const date = t.due_date ? new Date(t.due_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "";
            return (
              <div key={t.task_id} onClick={() => navigate(`/tasks/${t.task_id}`)}
                className="grid grid-cols-[12px_1fr_auto_auto_auto_auto] items-center gap-4 px-5 py-4 bg-[var(--s1)] border border-[var(--bdr)] rounded-xl cursor-pointer hover:border-[var(--bdrh)] hover:translate-x-[3px] transition-all group">
                <span className={`w-2.5 h-2.5 rounded-full ${dotClass} shadow-[0_0_0_3px_rgba(255,255,255,0.05)]`} />
                <div>
                  <h4 className="text-[14px] font-semibold text-[var(--t1)]">{t.title}</h4>
                  <p className="text-[12px] text-[var(--t3)] mt-0.5">{t.assignee_name || "Unassigned"}</p>
                </div>
                {t.priority && <span className={`text-[11px] font-mono font-medium ${priorityColors[t.priority] || "text-gray-400"}`}>{t.priority}</span>}
                <span className={`px-3.5 py-1 rounded-md text-[11px] font-semibold ${badgeClass}`}>{t.status}</span>
                {date && <span className="text-[12px] text-[var(--t3)] font-mono min-w-[55px] text-right">{date}</span>}
                <span className="text-[var(--t3)] text-[20px] group-hover:text-[var(--r)] group-hover:translate-x-[2px] transition-all">&rsaquo;</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ SOW HISTORY TAB ═══ */}
      {activeTab === "sow" && isRetainer && (
        <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl overflow-hidden">
          {retainerMonths.length === 0 ? (
            <div className="p-12 text-center text-[#666]">
              <p className="text-sm">No monthly entries yet</p>
              <p className="text-xs mt-1 text-[#555]">Monthly entries are auto-generated when retainer is confirmed</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-[var(--s1)]">
                  <th className="py-3 px-5 text-left text-[10px] font-mono uppercase tracking-[1.5px] text-[#888] font-medium">Month</th>
                  <th className="py-3 px-5 text-right text-[10px] font-mono uppercase tracking-[1.5px] text-[#888] font-medium">SOW Value</th>
                  <th className="py-3 px-5 text-right text-[10px] font-mono uppercase tracking-[1.5px] text-[#888] font-medium">Delivered</th>
                  <th className="py-3 px-5 text-left text-[10px] font-mono uppercase tracking-[1.5px] text-[#888] font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {retainerMonths.map((rm) => {
                  const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                  const mName = MONTH_NAMES[(rm.month_number || 1) - 1] || "\u2014";
                  return (
                    <tr key={rm.retainer_month_id || rm.month_number} className="border-t border-[var(--bdr)] hover:bg-[var(--s1)] transition-colors">
                      <td className="py-3 px-5 text-sm font-medium text-[#E5E5E5]">{mName} {rm.year || ""}</td>
                      <td className="py-3 px-5 text-sm text-right text-[#BBB]">₹{(rm.target_value || account.monthly_retainer_value || 0).toLocaleString("en-IN")}</td>
                      <td className="py-3 px-5 text-sm text-right font-semibold text-white">₹{(rm.actual_value || 0).toLocaleString("en-IN")}</td>
                      <td className="py-3 px-5">
                        <span className={`px-2.5 py-0.5 rounded-md text-[11px] font-medium ${rm.status === "invoiced" || rm.status === "billed" ? "bg-green-500/12 text-green-300" : rm.status === "in_progress" ? "bg-amber-500/12 text-amber-300" : "bg-gray-500/10 text-[#888]"}`}>
                          {rm.status || "pending"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === "activity" && (
        <div className="text-center py-16 text-[#666]">
          <p className="text-sm">Activity log coming soon</p>
        </div>
      )}

      {/* SOW Edit Modal — keyed on selected month so state resets on switch */}
      {showSOWEdit && (
        <SOWEditModal
          key={selectedSowMonth}
          sow={sow}
          delivered={displayedDelivered}
          monthLabel={sowMonthLabelLong(selectedSowMonth)}
          onSaveSow={saveSOW}
          onSaveDelivered={saveDelivered}
          onClose={() => setShowSOWEdit(false)}
        />
      )}

      {/* Convert to Retainer Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-2xl p-6 w-full max-w-lg">
            <h2 className="font-heading text-lg text-[var(--t1)] mb-1">Convert to Retainer</h2>
            <p className="text-[var(--t3)] text-sm mb-5">Set up retainer details for {account.company_name}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="text-xs text-[#AAA] font-medium">Monthly Retainer Fee (₹ in Lakhs) *</label>
                <input type="number" min="0" step="0.1" placeholder="e.g. 3.5" value={convertForm.fee}
                  onChange={(e) => setConvertForm((prev) => ({ ...prev, fee: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50"
                />
              </div>
              <div>
                <label className="text-xs text-[#AAA] font-medium">Start Date</label>
                <input type="date" value={convertForm.startDate}
                  onChange={(e) => setConvertForm((prev) => ({ ...prev, startDate: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50"
                />
              </div>
            </div>

            <div className="mb-5">
              <label className="text-xs text-[#AAA] font-medium">Service Type</label>
              <select value={convertForm.serviceType}
                onChange={(e) => setConvertForm((prev) => ({ ...prev, serviceType: e.target.value }))}
                className="w-full mt-1 px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 appearance-none">
                <option>Social & Digital Retainer</option>
                <option>Digital Content Production</option>
                <option>Influencer Marketing</option>
                <option>Performance Marketing</option>
                <option>Full Service Retainer</option>
              </select>
            </div>

            <div className="mb-5">
              <label className="text-xs text-[#AAA] font-medium mb-2 block">Monthly SOW (deliverables per month)</label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {ASSET_TYPES.map((t) => (
                  <div key={t}>
                    <label className="text-[10px] text-[var(--t3)] capitalize">{t}</label>
                    <input type="number" min="0" value={convertForm.sow[t]}
                      onChange={(e) => setConvertForm((prev) => ({ ...prev, sow: { ...prev.sow, [t]: parseInt(e.target.value) || 0 } }))}
                      className="w-full mt-1 px-2 py-1.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] text-center focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowConvertModal(false)} className="flex-1 py-2.5 border border-[var(--bdrh)] rounded-xl text-[#999] text-sm hover:border-[var(--bdrh)] transition-colors">Cancel</button>
              <button
                disabled={!convertForm.fee}
                onClick={async () => {
                  try {
                    await api.put(`/leads/${accountId}`, {
                      is_retainer: true,
                      monthly_retainer_value: parseFloat(convertForm.fee) * 100000,
                      retainer_start_date: convertForm.startDate,
                      retainer_service_type: convertForm.serviceType,
                      retainer_sow: convertForm.sow,
                    });
                    toast.success("Account converted to retainer!");
                    setShowConvertModal(false);
                    fetchData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to convert");
                  }
                }}
                className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl text-sm font-medium transition-colors">
                Confirm Retainer
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    </AnimatedPage>
  );
}
